import type { InvoiceInDTO, InvoiceLineInDTO, InvoiceOutDTO } from '@api/ringil-api.v2';

/**
 * RD-3062 — the form ↔ API mapping of `EditInvoiceSidebar`, moved out of the component unchanged so it
 * can be unit-tested. The component keeps the hooks, API calls, validation and UI; everything here is pure.
 */

export interface FormValues {
  issueDate?: string;
  taxPointDate?: string;
  paymentDueDate?: string;
  supplierCRN?: string;
  supplierName?: string;
  supplierTaxId?: string;
  customerCRN?: string;
  customerName?: string;
  customerTaxId?: string;
  bankIBAN?: string;
  bankBIC?: string;
  bankCode?: string;
  variableSymbol?: string;
  constantSymbol?: string;
  transactionCurrCode?: string;
  homeCurrCode?: string;
  currRate?: number;
  taxExclusiveTransactionCurrAmount?: number;
  taxInclusiveTransactionCurrAmount?: number;
  taxTransactionCurrAmount?: number;
  taxExclusiveHomeCurrAmount?: number;
  taxInclusiveHomeCurrAmount?: number;
  taxHomeCurrAmount?: number;
  taxRate?: number;
  invoiceLines: InvoiceLineInDTO[];
}

/** The form a loaded invoice is edited in. */
export const invoiceToFormValues = (data: InvoiceOutDTO): FormValues => ({
  issueDate: data.issueDate,
  taxPointDate: data.taxPointDate,
  paymentDueDate: data.paymentDueDate,
  supplierCRN: data.supplierCRN,
  supplierName: data.supplierName,
  supplierTaxId: data.supplierTaxId,
  customerCRN: data.customerCRN,
  customerName: data.customerName,
  customerTaxId: data.customerTaxId,
  bankIBAN: data.bankIBAN,
  bankBIC: data.bankBIC,
  bankCode: data.bankCode,
  variableSymbol: data.variableSymbol,
  constantSymbol: data.constantSymbol,
  transactionCurrCode: data.transactionCurrCode,
  homeCurrCode: data.homeCurrCode,
  currRate: data.currRate,
  taxExclusiveTransactionCurrAmount: data.taxExclusiveTransactionCurrAmount,
  taxInclusiveTransactionCurrAmount: data.taxInclusiveTransactionCurrAmount,
  taxTransactionCurrAmount: data.taxTransactionCurrAmount,
  taxExclusiveHomeCurrAmount: data.taxExclusiveHomeCurrAmount,
  taxInclusiveHomeCurrAmount: data.taxInclusiveHomeCurrAmount,
  taxHomeCurrAmount: data.taxHomeCurrAmount,
  taxRate: data.taxRate,
  invoiceLines: data.invoiceLines?.map(line => ({
    id: line.id,
    invoiceLineId: line.invoiceLineId,
    expeditionId: line.expeditionId,
    lineTransactionCurrAmount: line.lineTransactionCurrAmount,
    lineTaxInclusiveTransactionCurrAmount: line.lineTaxInclusiveTransactionCurrAmount,
    lineHomeCurrAmount: line.lineHomeCurrAmount,
    lineTaxInclusiveHomeCurrAmount: line.lineTaxInclusiveHomeCurrAmount,
    currency: line.currency,
  })) || [],
});

/** The body of `updateInvoice`: the edited form on top of the loaded invoice's identity and version. */
export const buildInvoiceUpdateRequest = (invoice: InvoiceOutDTO, formData: FormValues): InvoiceInDTO => {
  const updateData: InvoiceInDTO = {
    version: invoice.version,
    parentId: invoice.parentId || '',
    attachmentId: invoice.attachmentId,
    issueDate: formData.issueDate,
    taxPointDate: formData.taxPointDate,
    paymentDueDate: formData.paymentDueDate,
    supplierCRN: formData.supplierCRN,
    supplierName: formData.supplierName,
    supplierTaxId: formData.supplierTaxId,
    customerCRN: formData.customerCRN,
    customerName: formData.customerName,
    customerTaxId: formData.customerTaxId,
    bankIBAN: formData.bankIBAN,
    bankBIC: formData.bankBIC,
    bankCode: formData.bankCode,
    variableSymbol: formData.variableSymbol,
    constantSymbol: formData.constantSymbol,
    transactionCurrCode: formData.transactionCurrCode,
    homeCurrCode: formData.homeCurrCode,
    currRate: formData.currRate,
    taxExclusiveTransactionCurrAmount: formData.taxExclusiveTransactionCurrAmount,
    taxInclusiveTransactionCurrAmount: formData.taxInclusiveTransactionCurrAmount,
    taxTransactionCurrAmount: formData.taxTransactionCurrAmount,
    taxExclusiveHomeCurrAmount: formData.taxExclusiveHomeCurrAmount,
    taxInclusiveHomeCurrAmount: formData.taxInclusiveHomeCurrAmount,
    taxHomeCurrAmount: formData.taxHomeCurrAmount,
    taxRate: formData.taxRate,
    invoiceLines: formData.invoiceLines,
    invoiceMessages: [],
  };
  return updateData;
};

/** A blank invoice line, in the form's transaction currency, else the invoice's, else none. */
export const newInvoiceLine = (values: FormValues, invoice: InvoiceOutDTO | null): InvoiceLineInDTO => {
  const defaultCurrency = values.transactionCurrCode || invoice?.transactionCurrCode || '';

  return {
    invoiceLineId: undefined,
    expeditionId: undefined,
    lineTransactionCurrAmount: 0,
    lineTaxInclusiveTransactionCurrAmount: 0,
    lineHomeCurrAmount: 0,
    lineTaxInclusiveHomeCurrAmount: 0,
    currency: defaultCurrency,
  };
};
